/**
 * AI Priority Queue (Phase 9H)
 * Smart lead queues for prioritized outreach
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Target, Flame, DollarSign, TrendingUp, Phone, FileText, ClipboardList, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUEUE_ICONS = {
  todays_best: Flame,
  top_opportunities: Target,
  highest_revenue: DollarSign,
  highest_close_probability: TrendingUp,
  needs_contact: Phone,
  needs_audit: FileText,
  needs_proposal: ClipboardList,
  needs_followup: Clock
};

const AIPriorityQueue = ({ user, onNavigate }) => {
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  useEffect(() => {
    const fetchQueues = async () => {
      try {
        const response = await fetch(`${API}/phase9/priority-queue-list`, {
          headers: authHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setQueues(data.queues || []);
          // Auto-select first queue
          if (data.queues && data.queues.length > 0) {
            handleSelectQueue(data.queues[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching queues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQueues();
  }, [authHeaders]);

  const handleSelectQueue = async (queueId) => {
    setSelectedQueue(queueId);
    setLeadsLoading(true);
    try {
      const response = await fetch(`${API}/phase9/priority-queue/${queueId}?limit=20`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching queue leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const getScoreBadgeClass = (label) => {
    switch (label) {
      case 'Hot': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Warm': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Cold': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  return (
    <div className="page-stack" data-testid="ai-priority-queue">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9H — AI Priority Engine</p>
          <h2>AI Priority Queue</h2>
          <div className="hero-stats">
            <span>{queues.length} Smart Queues</span>
            <span>{leads.length} Leads in Current Queue</span>
          </div>
        </div>
      </section>

      {/* Queue Selector */}
      <section className="glass-panel">
        <h3 className="text-lg font-semibold text-white/90 mb-4">Select Priority Queue</h3>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-24 bg-white/10" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {queues.map((queue) => {
              const Icon = QUEUE_ICONS[queue.id] || Target;
              const isSelected = selectedQueue === queue.id;
              return (
                <button
                  key={queue.id}
                  onClick={() => handleSelectQueue(queue.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-[var(--gold)] bg-[var(--gold)]/10'
                      : 'border-white/10 bg-white/5 hover:border-white/30'
                  }`}
                  data-testid={`queue-btn-${queue.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-[var(--gold)]' : 'text-white/60'}`} />
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${isSelected ? 'text-white' : 'text-white/80'}`}>
                    {queue.name}
                  </p>
                  <Badge className={isSelected ? 'bg-[var(--gold)]/20 text-[var(--gold)]' : 'bg-white/10 text-white/60'}>
                    {queue.count} leads
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Queue Leads */}
      <section className="glass-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white/90">
            {queues.find(q => q.id === selectedQueue)?.name || 'Select a queue'}
          </h3>
          <Badge className="bg-white/10 text-white/60">{leads.length} leads</Badge>
        </div>

        {leadsLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full bg-white/10" />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No leads in this queue</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leads.map((lead, idx) => (
              <div
                key={lead.id}
                className="flex items-center justify-between p-4 border border-white/10 rounded-xl hover:bg-white/[0.02] hover:border-[var(--line-gold)] cursor-pointer transition-all"
                onClick={() => onNavigate('detail', lead)}
                data-testid={`queue-lead-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center border border-[var(--gold)]/30">
                    <span className="text-sm font-bold text-[var(--gold)]">{idx + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white/90">{lead.clinic_name}</h4>
                    <p className="text-xs text-white/60">{lead.city}, {lead.state}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {lead.ai_lead_score !== null && lead.ai_lead_score !== undefined && (
                    <Badge className={`${getScoreBadgeClass(lead.ai_lead_score_label)} text-xs`}>
                      {lead.ai_lead_score}
                    </Badge>
                  )}
                  {lead.ai_sales_intelligence?.ai_priority_score && (
                    <Badge className="bg-[var(--gold)]/20 text-[var(--gold)] text-xs">
                      Priority: {lead.ai_sales_intelligence.ai_priority_score}
                    </Badge>
                  )}
                  {lead.ai_sales_intelligence?.ai_close_probability && (
                    <Badge className="bg-green-500/20 text-green-300 text-xs">
                      {lead.ai_sales_intelligence.ai_close_probability}% Close
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AIPriorityQueue;
